import React, { useState, useEffect, useMemo } from 'react'

import {
    Dialog,
    DialogContent,
    IconButton,
    Box,
    useTheme,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Button,
    Divider,
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { useCampaign } from '../../../../hooks/Outreach/useCampaign'
import { useGuestCards } from '../../../../hooks/Outreach/useGuestCards'
import {
    GuestCardFormRequest,
    GuestCardFormResponse,
} from '../../../../hooks/Outreach/useGuestCardForm'
import {
    Prospect,
    DetailedCampaignProspect,
} from '../../../../models/Outreach/types'
import { toast } from 'react-toastify'
import {
    isDetailedCampaignProspect,
    useLeads,
} from '../../../../hooks/Outreach/useLeads'
import { GuestCardFormFields } from './GuestCardFormFields'
import { useAptConfig } from '../../../../hooks/useAptConfig'
import { usePremiums } from '../../../../hooks/usePremiums'
import { Container } from '../../../../components'
import { useUser } from '../../../../hooks/useUser'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface ConvertToLeadDialogProps {
    open: boolean
    onClose: () => void
    prospectOrCampaignProspect?: Prospect | DetailedCampaignProspect
}

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            maxWidth: '900px',
            height: '85vh',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    dialogContent: {
        padding: theme.spacing(3),
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    closeButton: {
        color: theme.palette.grey[500],
    },
    stepContent: {
        padding: theme.spacing(3, 0),
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    formControl: {
        marginBottom: theme.spacing(2),
        minWidth: '100%',
    },
    sectionTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    sectionDescription: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(3),
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    guestCardFormContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(2),
        minHeight: 0,
    },
}))

export const ConvertToLeadDialog: React.FC<ConvertToLeadDialogProps> = ({
    open,
    onClose,
    prospectOrCampaignProspect,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace.id

    // Use a dummy apartmentId if not available since we only need the mutation
    const { convertToLeadMutation } = useLeads({
        apartmentId: apartmentId || 0,
        enabled: false, // We don't need to fetch data, just use the mutation
    })

    // Extract prospect and campaign prospect info
    const isCampaignProspect = prospectOrCampaignProspect
        ? isDetailedCampaignProspect(prospectOrCampaignProspect)
        : false
    const prospect: Prospect | null = prospectOrCampaignProspect
        ? isCampaignProspect
            ? (prospectOrCampaignProspect as DetailedCampaignProspect).prospect
            : (prospectOrCampaignProspect as Prospect)
        : null
    const campaignProspect: DetailedCampaignProspect | null = isCampaignProspect
        ? (prospectOrCampaignProspect as DetailedCampaignProspect)
        : null

    // Step management
    const [activeStep, setActiveStep] = useState(0)

    // Section 1: Campaign selection
    const [selectedCampaignId, setSelectedCampaignId] = useState<
        number | string | null
    >(null)

    // Section 2: Guest card selection
    const [selectedGuestCardId, setSelectedGuestCardId] = useState<
        number | string | null
    >(null)

    // Determine if prospect is in a campaign
    const isInCampaign = useMemo(() => {
        if (campaignProspect) return true
        if (prospect?.active_campaign) return true
        return false
    }, [campaignProspect, prospect])

    // Get the campaign ID (either from existing campaign prospect or selected)
    const finalCampaignId = useMemo(() => {
        if (campaignProspect) return campaignProspect.campaign.id
        if (prospect?.active_campaign) return prospect.active_campaign.id
        return selectedCampaignId
    }, [campaignProspect, prospect, selectedCampaignId])

    // Fetch campaigns and guest cards
    const { campaigns, isLoadingCampaigns } = useCampaign()
    const { guestCards, isLoadingGuestCards } = useGuestCards()

    // Fetch unit configs for the form
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const { premiums } = usePremiums()

    // Construct guest card form data from selected guest card and prospect
    const guestCardFormData = useMemo<GuestCardFormResponse | null>(() => {
        if (!selectedGuestCardId || !guestCards) return null

        const selectedGuestCard = guestCards.find(
            (card) => card.id === selectedGuestCardId,
        )

        if (!selectedGuestCard) return null

        // Convert unit configs to the format expected by the form
        // Cast to UnitConfigAreaCounts since the form expects bed_count and bath_count
        const unitConfigs = (
            unitConfigList.filter((config) => config.shows_on_schedule) || []
        ).map((config) => ({
            ...config,
            bed_count: (config as any).bed_count ?? null,
            bath_count: (config as any).bath_count ?? null,
        }))

        return {
            guest_card: selectedGuestCard,
            name: prospect?.name || null,
            email: prospect?.email?.value || null,
            phone: prospect?.phone?.value || null,
            unit_configs: unitConfigs,
            premiums: premiums.filter((premium) => premium.is_active),
            apartment_name: '', // This might need to come from somewhere else
        }
    }, [selectedGuestCardId, guestCards, prospect, unitConfigList, premiums])

    // Determine steps based on whether prospect is in campaign
    const steps = useMemo(() => {
        const stepList = []
        if (!isInCampaign) {
            stepList.push('Select Campaign')
        }
        stepList.push('Select Guest Card')
        stepList.push('Fill Guest Card Form')
        return stepList
    }, [isInCampaign])

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (!open) return
        setActiveStep(0)
        setSelectedCampaignId(null)
        setSelectedGuestCardId(null)
        convertToLeadMutation.reset()
    }, [open])

    const handleNext = () => {
        if (activeStep === 0 && !isInCampaign) {
            // Validate campaign selection
            if (!selectedCampaignId) {
                toast.error('Please select a campaign')
                return
            }
        } else if (
            (activeStep === 0 && isInCampaign) ||
            (activeStep === 1 && !isInCampaign)
        ) {
            // Validate guest card selection
            if (!selectedGuestCardId) {
                toast.error('Please select a guest card')
                return
            }
            if (!guestCardFormData) {
                return
            }
        }
        setActiveStep((prev) => prev + 1)
    }

    const handleBack = () => {
        setActiveStep((prev) => prev - 1)
    }

    const handleFormSubmit = async (formData: GuestCardFormRequest) => {
        if (!selectedGuestCardId) {
            toast.error('Guest card ID is missing')
            return
        }

        if (!finalCampaignId) {
            toast.error('Campaign ID is missing')
            return
        }

        if (!prospect?.id) {
            toast.error('Prospect ID is missing')
            return
        }

        // Construct the request body similar to useGuestCardForm but with additional fields
        const requestBody: GuestCardFormRequest & {
            guest_card_id: number | string
            campaign_id: number | string
            prospect_id?: number
            campaign_prospect_id?: number
        } = {
            ...formData,
            guest_card_id: selectedGuestCardId,
            campaign_id: finalCampaignId,
        }

        // Add prospect_id or campaign_prospect_id based on whether prospect is in campaign
        if (campaignProspect) {
            requestBody.campaign_prospect_id = campaignProspect.id
        } else {
            requestBody.prospect_id = prospect.id
        }

        convertToLeadMutation.mutate(requestBody, {
            onSuccess: () => {
                onClose()
            },
        })
    }

    const renderCampaignSelection = () => (
        <Box className={classes.stepContent}>
            <Typography className={classes.sectionTitle}>
                Select Campaign
            </Typography>
            <Typography className={classes.sectionDescription}>
                Choose a campaign for this prospect before converting to a lead.
            </Typography>
            {isLoadingCampaigns ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                >
                    <CircularProgress />
                </Box>
            ) : (
                <FormControl className={classes.formControl} variant="outlined">
                    <InputLabel>Campaign</InputLabel>
                    <Select
                        value={selectedCampaignId || ''}
                        onChange={(e) =>
                            setSelectedCampaignId(e.target.value as number)
                        }
                        label="Campaign"
                    >
                        {campaigns?.map((campaign) => (
                            <MenuItem key={campaign.id} value={campaign.id}>
                                {campaign.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    )

    const renderGuestCardSelection = () => (
        <Box className={classes.stepContent}>
            <Typography className={classes.sectionTitle}>
                Select Guest Card
            </Typography>
            <Typography className={classes.sectionDescription}>
                Choose a guest card template to collect information from the
                prospect.
            </Typography>
            {isLoadingGuestCards ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                >
                    <CircularProgress />
                </Box>
            ) : (
                <FormControl className={classes.formControl} variant="outlined">
                    <InputLabel>Guest Card</InputLabel>
                    <Select
                        value={selectedGuestCardId || ''}
                        onChange={(e) =>
                            setSelectedGuestCardId(e.target.value as number)
                        }
                        label="Guest Card"
                    >
                        {guestCards?.map((card) => (
                            <MenuItem key={card.id} value={card.id}>
                                {card.card_name}
                                {card.default && (
                                    <Typography
                                        component="span"
                                        style={{
                                            marginLeft: theme.spacing(1),
                                            fontSize: '0.75rem',
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        (Default)
                                    </Typography>
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    )

    const renderGuestCardForm = () => {
        if (!guestCardFormData) {
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="300px"
                >
                    <CircularProgress />
                </Box>
            )
        }

        return (
            <Box className={classes.stepContent}>
                <Typography className={classes.sectionTitle}>
                    Guest Card Information
                </Typography>
                <Typography className={classes.sectionDescription}>
                    Please fill out the guest card form below.
                </Typography>
                <Box className={classes.guestCardFormContainer}>
                    <GuestCardFormFields
                        guestCardFormData={guestCardFormData}
                        prospect={prospect}
                        onSubmit={handleFormSubmit}
                        isLoading={convertToLeadMutation.isLoading}
                    />
                </Box>
            </Box>
        )
    }

    const getCurrentStepContent = () => {
        if (!isInCampaign && activeStep === 0) {
            return renderCampaignSelection()
        }
        if (
            (!isInCampaign && activeStep === 1) ||
            (isInCampaign && activeStep === 0)
        ) {
            return renderGuestCardSelection()
        }
        return renderGuestCardForm()
    }

    const canProceed = () => {
        if (!isInCampaign && activeStep === 0) {
            return !!selectedCampaignId
        }
        if (
            (!isInCampaign && activeStep === 1) ||
            (isInCampaign && activeStep === 0)
        ) {
            return !!selectedGuestCardId && !!guestCardFormData
        }
        return true
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className={classes.dialog}
            PaperProps={{
                style: {
                    borderRadius: 16,
                },
            }}
        >
            <Container style={{ padding: theme.spacing(2) }}>
                <Box
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 'bold',
                        flex: 1,
                    }}
                >
                    Convert to Lead
                </Box>
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Container>
            <DialogContent className={classes.dialogContent}>
                {!prospectOrCampaignProspect ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="300px"
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box flexShrink={0}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            <Divider style={{ margin: theme.spacing(2, 0) }} />
                        </Box>

                        <Box
                            flex={1}
                            minHeight={0}
                            display="flex"
                            flexDirection="column"
                        >
                            {getCurrentStepContent()}
                        </Box>
                    </>
                )}

                {activeStep < steps.length - 1 && (
                    <Box className={classes.actionButtons}>
                        <Button onClick={onClose}>Cancel</Button>
                        {activeStep > 0 && (
                            <Button onClick={handleBack}>Back</Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={!canProceed()}
                        >
                            Next
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    )
}
